import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import {
    MailOutline,
    Wysiwyg,
    Folder as FolderIcon,
    MarkunreadMailboxOutlined,
    TextsmsOutlined,
    SettingsPhone,
} from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import { UseQueryResult } from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useHistory } from 'react-router-dom';

import WhatsAppIcon from 'assets/images/WhatsAppIcon';
import {
    ContentTypeForComparison,
    LandingPage,
    Template,
    ContentModalContentType,
    RootContentFolder,
    SMS,
    VoiceContent,
} from 'domains/content/types';
import ArchiveTableItemModal from 'domains/core/components/ArchiveTableItemModal';
import DeleteTableItemModal from 'domains/core/components/DeleteTableItemModal';
import FolderBreadcrumb from 'domains/core/components/folders/FolderBreadcrumb';
import MoveItemModal from 'domains/core/components/folders/MoveItemModal';
import RenameFolderModal from 'domains/core/components/folders/RenameFolderModal';
import { FolderBreadcrumbData, FolderLineage, FolderModalTypes } from 'domains/core/components/folders/types';
import Table, { ColumnOption, renderDate } from 'domains/core/components/Table';
import TableEmptyState from 'domains/core/components/Table/TableEmptyState';
import TableOffsetPagination from 'domains/core/components/TableOffsetPagination';
import UnarchiveTableItemModal from 'domains/core/components/UnarchiveTableItemModal';
import useFeatureSettings from 'hooks/queries/useFeatureSettings';
import useGetFolderLineage from 'hooks/queries/useGetFolderLineage';
import useContentQueryManager from 'hooks/useContentQueryParams';
import { SortDirection } from 'models/enums';
import { Folder, FolderLocation } from 'models/types';
import { getIsFolderContentActive, removeTypeFromId } from 'utils/folders';

import ContentStatusText, { ContentStatus } from './ContentStatusText';
import ContentTableOptionsCell from './ContentTableOptionsCell';
import { getContentCreatePath, getContentViewPath, getContentType } from './utils';

export type ContentTablePaginationProps = {
    page: number;
    totalPages: number;
    onPreviousPage: () => void;
    onNextPage: () => void;
    rowsPerPage: number;
    rowsPerPageOptions: number[];
    onRowsPerPageChange: (rowsPerPage: number) => void;
};

export type Props<ContentType> = {
    contentQuery: UseQueryResult<ContentType[], unknown>;
    hasActiveFilters?: boolean;
    searchTerm?: string;
    shouldShowEmptyTable?: Boolean;
    canManageContent?: boolean;
    pagination?: ContentTablePaginationProps;
    onSort?: (sortColumn: string, direction: SortDirection) => void;
    manualSortBy?: boolean;
};

const ContentTable = <ContentType extends Template | SMS | LandingPage | Folder | VoiceContent>({
    contentQuery: { data: queryData, isLoading: isQueryLoading, isRefetching, refetch: refetchTableData },
    hasActiveFilters,
    searchTerm,
    shouldShowEmptyTable,
    canManageContent = true,
    pagination,
    onSort,
    manualSortBy = false,
}: Props<ContentType>) => {
    const { data: featureSettings, isSuccess: isFeatureSettingsSuccess } = useFeatureSettings();
    const {
        contentDraft: hasContentDraft,
        whatsappContent: hasWhatsappContent,
        accessCenter: hasAccessCenterFlag,
    } = useFlags();

    const hasEmailEditor = isFeatureSettingsSuccess && featureSettings?.email_enabled !== false;
    const hasSmsEditor = isFeatureSettingsSuccess && featureSettings?.sms_enabled !== false;
    const hasDirectMailEditor = isFeatureSettingsSuccess && featureSettings?.directmail_enabled;

    const history = useHistory();

    const { changeViewParams, folderId } = useContentQueryManager();

    const folderContentIsActive = getIsFolderContentActive(folderId);
    const { data: folderBreadcrumbData, refetch: refetchFolderBreadcrumbData } = useGetFolderLineage({
        folderId,
        folderLocation: FolderLocation.CONTENT_FOLDER,
        reactQueryOptions: {
            enabled: folderContentIsActive,
        },
    });
    const folderBreadcrumbDataWithType = folderBreadcrumbData?.map((breadcrumbData: FolderLineage) => ({
        ...breadcrumbData,
        type: ContentTypeForComparison.FOLDER,
    }));

    const [activeContent, setActiveContent] = useState(null);
    const [activeFolder, setActiveFolder] = useState(RootContentFolder);
    const [activeModal, setActiveModal] = useState('');

    useEffect(() => {
        if (folderBreadcrumbData?.length > 0) {
            folderBreadcrumbData.forEach((folder: FolderBreadcrumbData) => {
                if (folder.level === 1) {
                    setActiveFolder({ ...folder, type: ContentTypeForComparison.FOLDER });
                }
            });
        } else {
            setActiveFolder(RootContentFolder);
        }
    }, [folderBreadcrumbData, setActiveFolder]);

    const data: object[] = useMemo(
        () =>
            queryData
                ?.map((content: ContentType) => {
                    const isArchiveValue =
                        content.type !== ContentTypeForComparison.FOLDER ? (content as any).isArchived || false : false;

                    return {
                        ...content,
                        dateCreated: new Date(content.createdTimestamp),
                        dateUpdated: content.lastModifiedTimestamp
                            ? new Date(content.lastModifiedTimestamp)
                            : undefined,
                        id: `${content.type}:${content.id}`,
                        isArchived: isArchiveValue,
                    };
                })
                .filter((content: ContentType) => {
                    const showEmailContent = hasEmailEditor || content.type !== ContentTypeForComparison.EMAIL;
                    const showSmsContent = hasSmsEditor || content.type !== ContentTypeForComparison.SMS;
                    const showDirectMailContent =
                        hasDirectMailEditor || content.type !== ContentTypeForComparison.DIRECT_MAIL;
                    const showWhatsappContent =
                        hasWhatsappContent || content.type !== ContentTypeForComparison.WHATSAPP;

                    return showEmailContent && showSmsContent && showDirectMailContent && showWhatsappContent;
                }),
        [hasDirectMailEditor, hasEmailEditor, hasSmsEditor, hasWhatsappContent, queryData]
    );

    const renderContentIcon = (val: any) => {
        if (val.type === ContentTypeForComparison.DIRECT_MAIL) return <MarkunreadMailboxOutlined color="info" />;
        if (val.type === ContentTypeForComparison.EMAIL) return <MailOutline color="info" />;
        if (val.type === ContentTypeForComparison.LANDING_PAGE) return <Wysiwyg color="info" />;
        if (val.type === ContentTypeForComparison.SMS) return <TextsmsOutlined color="info" />;
        if (val.type === ContentTypeForComparison.FOLDER) return <FolderIcon color="primary" />;
        if (val.type === ContentTypeForComparison.VOICE) return <SettingsPhone color="info" />;
        if (val.type === ContentTypeForComparison.WHATSAPP) return <WhatsAppIcon />;
    };

    const handleModalOpen = useCallback(
        (content: ContentModalContentType, modalType: FolderModalTypes, contentType?: ContentTypeForComparison) => {
            const newActiveContent = !!contentType ? { ...content, type: contentType } : content;
            setActiveContent(newActiveContent);
            setActiveModal(modalType);
        },
        []
    );

    const columns: ColumnOption[] = useMemo(() => {
        const renderNameCell = (val: any) => {
            const name = `${val.row.original.isLayout ? 'TEMPLATE - ' : ''}${val.cell.value}`;

            return (
                <Stack alignItems="center" direction="row" spacing={2} color="warning.dark">
                    {renderContentIcon(val.row.original)}
                    <Typography>{name}</Typography>
                </Stack>
            );
        };

        const nameColumn: ColumnOption = {
            Header: 'Name',
            accessor: 'name',
            sortType: 'string',
            align: 'left',
            Cell: (val: any) => renderNameCell(val),
            ...(manualSortBy ? { customSort: 'name' } : {}),
        };

        const statusColumn: ColumnOption = {
            Header: 'Status',
            accessor: 'status',
            align: 'center',
            Cell: (val: any) => {
                const status = val.row.original.status;

                return <ContentStatusText status={status} />;
            },
            ...(manualSortBy ? { customSort: 'status' } : {}),
        };

        const dateCreatedColumn: ColumnOption = {
            Header: 'Date Created',
            accessor: 'dateCreated',
            sortType: 'datetime',
            Cell: renderDate,
            ...(manualSortBy ? { customSort: 'created_timestamp' } : {}),
        };
        const dateUpdatedColumn: ColumnOption = {
            Header: 'Date Updated',
            accessor: 'dateUpdated',
            sortType: 'datetime',
            Cell: renderDate,
            ...(manualSortBy ? { customSort: 'last_modified_timestamp' } : {}),
        };
        const typeColumn: ColumnOption = {
            Header: 'Type',
            accessor: 'type',
            Cell: (val: any) => {
                const type = getContentType(val.row.original);
                return type.toLowerCase();
            },
            ...(manualSortBy ? { customSort: 'type' } : {}),
        };
        const moreOptionsColumn: ColumnOption = {
            Header: '',
            accessor: 'id',
            align: 'right',
            disableSortBy: true,
            Cell: (val: any) => (
                <ContentTableOptionsCell
                    handleModalOpen={handleModalOpen}
                    val={val}
                    canManageContent={canManageContent}
                />
            ),
        };

        // Conditionally include status column only if contentDraft feature flag is enabled
        const baseColumns = [nameColumn, typeColumn, dateCreatedColumn, dateUpdatedColumn, moreOptionsColumn];
        const columnsWithStatus = [
            nameColumn,
            statusColumn,
            typeColumn,
            dateCreatedColumn,
            dateUpdatedColumn,
            moreOptionsColumn,
        ];

        // Conditionally include status column only if contentDraft feature flag is enabled
        return hasContentDraft ? columnsWithStatus : baseColumns;
    }, [handleModalOpen, hasContentDraft, canManageContent, manualSortBy]);

    // TODO: update to use real row type here
    const onRowClick = (row: any) => {
        const id = removeTypeFromId(row.original.id);
        if (row.original.type === ContentTypeForComparison.FOLDER) {
            changeViewParams({ newFolderId: id });
        } else {
            // Use view path only if flag is true and status is published or live
            // Otherwise use create path
            // Landing pages should always open in edit/builder mode, not view mode
            const isLandingPage = row.original.type === ContentTypeForComparison.LANDING_PAGE;
            const isPublishedOrLive =
                row.original.status === ContentStatus.PUBLISHED || row.original.status === ContentStatus.LIVE;
            const isDraft = row.original.status === ContentStatus.DRAFT;
            const openDraftInPreviewWhenAccessCentre =
                Boolean(hasAccessCenterFlag) && hasContentDraft && isDraft && !isLandingPage;
            const shouldUseViewPath =
                hasContentDraft && !isLandingPage && (isPublishedOrLive || openDraftInPreviewWhenAccessCentre);
            const path = shouldUseViewPath
                ? getContentViewPath({
                      contentType: row.original.type,
                      id,
                      parentFolderId: folderId,
                      status: row.original.status,
                  })
                : getContentCreatePath({
                      contentType: row.original.type,
                      id,
                      isCopy: false,
                      parentFolderId: folderId,
                      status: row.original.status,
                  });
            history.push(path);
        }
    };

    const handleBreadcrumbClick = (newFolderId: number) => changeViewParams({ newFolderId });

    return (
        <>
            <DeleteTableItemModal
                activeItem={activeContent}
                handleModalClose={() => setActiveModal('')}
                isOpen={activeModal === FolderModalTypes.DELETE}
                refetchTableData={refetchTableData}
                resetActiveItem={() => setActiveContent(null)}
            />
            <ArchiveTableItemModal
                activeItem={activeContent}
                handleModalClose={() => setActiveModal('')}
                isOpen={activeModal === FolderModalTypes.ARCHIVE}
                refetchTableData={refetchTableData}
                resetActiveItem={() => setActiveContent(null)}
            />
            <UnarchiveTableItemModal
                activeItem={activeContent}
                handleModalClose={() => setActiveModal('')}
                isOpen={activeModal === FolderModalTypes.UNARCHIVE}
                refetchTableData={refetchTableData}
                resetActiveItem={() => setActiveContent(null)}
            />
            <RenameFolderModal
                activeFolder={activeContent}
                folderLocation={FolderLocation.CONTENT_FOLDER}
                handleModalClose={() => setActiveModal('')}
                isOpen={activeModal === FolderModalTypes.RENAME}
                refetch={folderContentIsActive ? refetchFolderBreadcrumbData : refetchTableData}
                resetActiveFolder={() => setActiveContent(null)}
            />
            <MoveItemModal
                activeItem={activeContent}
                activeFolder={activeFolder}
                folderLocation={FolderLocation.CONTENT_FOLDER}
                handleModalClose={() => setActiveModal('')}
                isOpen={activeModal === FolderModalTypes.MOVE}
                refetch={refetchTableData}
                resetActiveItem={() => setActiveContent(null)}
            />
            <FolderBreadcrumb
                data={folderBreadcrumbDataWithType}
                handleBreadcrumbClick={handleBreadcrumbClick}
                handleModalOpen={handleModalOpen}
                showDeleteFolderOption={shouldShowEmptyTable}
            />
            <Table
                data={data ?? []}
                columns={columns}
                defaultSortingRules={[{ id: 'dateUpdated', desc: true }]}
                isLoading={isQueryLoading || isRefetching}
                isRefetching={isRefetching}
                manualSortBy={manualSortBy}
                onSort={onSort}
                onRowClick={onRowClick}
                refetch={refetchTableData}
            />
            {pagination && (
                <TableOffsetPagination
                    page={pagination.page}
                    totalPages={pagination.totalPages}
                    onPreviousPage={pagination.onPreviousPage}
                    onNextPage={pagination.onNextPage}
                    rowsPerPage={pagination.rowsPerPage}
                    rowsPerPageOptions={pagination.rowsPerPageOptions}
                    onRowsPerPageChange={pagination.onRowsPerPageChange}
                    testIdPrefix="content-table-pagination"
                />
            )}
            {shouldShowEmptyTable && (
                <TableEmptyState
                    folderLocation={FolderLocation.CONTENT_FOLDER}
                    hasActiveFilters={hasActiveFilters}
                    searchTerm={searchTerm}
                />
            )}
        </>
    );
};

export default memo(ContentTable) as typeof ContentTable;
